export interface ClusterVisibilityRule {
    namePatterns: string[];
    mersyGroupFunction: string | string[];
    permissionProperties: string[];
    requiredPermissions?: string[];
}

export interface UserDefaults {
    mersyGroupFunctions?: string[];
    hasProductTypeRoles?: boolean;
    hasLineOfBusinessRoles?: boolean;
    [key: string]: any;
}

export const CLUSTER_VISIBILITY_RULES: ClusterVisibilityRule[] = [
    {
        namePatterns: ['safety system excellance tool'],
        mersyGroupFunction: 'SSX',
        permissionProperties: [
            'isMersyAdminSafetySystemExcellence',
            'isMersyAssessorSafetySystemExcellence',
            'isMersyReporterSafetySystemExcellence'
        ]
    },
    {
        namePatterns: ['investment controlling', 'inco'],
        mersyGroupFunction: 'INCO',
        permissionProperties: [
            'isMersyAdminINCO',
            'isMersyUserINCO'
        ]
    },
    {
        namePatterns: ['tax@cdp', 'tax'],
        mersyGroupFunction: 'TAX',
        permissionProperties: [
            'isMersyCDPTaxAdmin',
            'isMersyCDPTaxRevoker',
            'isMersyCDPTaxReporter',
            'isMersyCDPTaxViewer'
        ]
    },
    {
        namePatterns: ['market query', 'marketquery'],
        mersyGroupFunction: 'MQY',
        permissionProperties: [
            'isMersyAdminMarketQueryUser',
            'isMersyOwnerMarketQueryUser',
            'isMersyReporterMarketQueryUser',
            'isMersyRevokerMarketQueryUser',
            'isMersyDelegateMarketQueryUser',
            'isMersyViewerMarketQueryUser'
        ],
        requiredPermissions: ['hasProductTypeRoles', 'hasLineOfBusinessRoles']
    },
    {
        namePatterns: ['global branch excellence', 'branchexcellence'],
        mersyGroupFunction: 'GBE',
        permissionProperties: [
            'isMersyAdminBranchExcellenceUser',
            'isMersyBranchManagerBranchExcellenceUser',
            'isMersyCoachBranchExcellenceUser',
            'isMersyViewerBranchExcellenceUser'
        ]
    },
    {
        namePatterns: ['eox ni ops reporting', 'niopsuser'],
        mersyGroupFunction: 'EOX',
        permissionProperties: [
            'isMersyNiOpsAdminUser',
            'isMersyNiOpsReporterUser'
        ]
    },
    {
        namePatterns: ['mersy@cdp', 'mersyuser'],
        mersyGroupFunction: [
            'MFG',
            'QM',
            'FLD',
            'ESG',
            'OSH',
            'MOD',
            'NPS',
            'HR',
            'DTX',
            'SER',
            'ITS',
            'NI Ops',
            'MOD Ops',
            'SER Ops',
            'SER Ops-FLP',
            'FPA',
        ],
        permissionProperties: ['IsMersyUser']
    }
];

export function findClusterRule(clusterName: string): ClusterVisibilityRule | undefined {
    if (!clusterName) {
        return undefined;
    }

    const normalizedName = clusterName.toLowerCase();
    return CLUSTER_VISIBILITY_RULES.find(rule =>
        rule.namePatterns.some(pattern => normalizedName.includes(pattern.toLowerCase()))
    );
}

export function hasAnyPermission(defaults: UserDefaults, permissionProperties: string[]): boolean {
    if (!permissionProperties || permissionProperties.length === 0) {
        return false;
    }

    return permissionProperties.some(property => defaults[property] === true);
}

export function hasGroupFunction(
    defaults: UserDefaults,
    requiredGroupFunction: string | string[]
): boolean {
    if (!requiredGroupFunction || requiredGroupFunction === '') {
        return true;
    }

    const userGroupFunctions = defaults.mersyGroupFunctions;
    if (!userGroupFunctions || !Array.isArray(userGroupFunctions) || userGroupFunctions.length === 0) {
        return false;
    }

    const normalizedUserGroupFunctions = userGroupFunctions.map(gf => gf.toLowerCase().trim());

    if (Array.isArray(requiredGroupFunction)) {
        return requiredGroupFunction.some(requiredGf =>
            normalizedUserGroupFunctions.includes(requiredGf.toLowerCase().trim())
        );
    }

    return normalizedUserGroupFunctions.includes(requiredGroupFunction.toLowerCase().trim());
}

export function hasRequiredPermissions(
    defaults: UserDefaults,
    requiredPermissions: string[]
): boolean {
    if (!requiredPermissions || requiredPermissions.length === 0) {
        return true;
    }

    return requiredPermissions.every(property => defaults[property] === true);
}

export function isClusterVisible(defaults: UserDefaults, clusterName: string): boolean {
    if (!clusterName) {
        return true;
    }

    const rule = findClusterRule(clusterName);

    if (!rule) {
        return true;
    }

    const hasRequiredPermissionsCheck = !rule.requiredPermissions ||
        hasRequiredPermissions(defaults, rule.requiredPermissions);

    const hasOptionalPermissionsCheck = hasAnyPermission(defaults, rule.permissionProperties);

    const hasGroupFunctionCheck = hasGroupFunction(defaults, rule.mersyGroupFunction);

    return hasRequiredPermissionsCheck && hasOptionalPermissionsCheck && hasGroupFunctionCheck;
}
